import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { useHeaderNavigationContext } from '@fogia/core-web/contexts/HeaderNavigationContext';
import decodeHTMLEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import Link from '@fogia/core-web/components/Link';
import styled from 'libs/styled';
import Text from 'components/Text';
import { validThemes, type ThemeName } from 'config/branding/themes';
import { above } from '@fogia/core-web/utils/mediaqueries';

interface BreadcrumbItem {
    slug: string | null;
    title: string;
}

interface BreadcrumbsProps {
    breadcrumbs?: BreadcrumbItem[];
    [key: string]: unknown;
}

const BreadcrumbShell = styled('div')`
    pointer-events: none;
    width: max-content;
    max-width: 100%;
    margin: calc(var(--header-height) + 3px) 10px 3px 10px;

    ${above['desktop.sm']} {
        transform: translateY(-100%);
        margin-top: calc(var(--header-height) - 10px); // 10px is the margin of the header
    }
`;

const FlowWrapper = styled(BreadcrumbShell)`
    position: relative;
`;

const AbsoluteWrapper = styled(BreadcrumbShell)`
    position: absolute;
    left: 0;
    top: 0;
`;

const StyledBreadcrumbs = styled('div')`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    overflow: auto hidden;
    max-width: 100%;
    pointer-events: auto;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const Separator = styled('span')`
    color: var(--opacity-primary-15);
    white-space: nowrap;
`;

const CurrentItem = styled('span')`
    color: var(--button-tertiery-text-disabled);
    white-space: nowrap;
`;

const LinkItem = styled(Link)`
    white-space: nowrap;
    color: var(--button-tertiery-text);
`;

interface ContentPageData {
    breadcrumbs?: { slug: string; title: string }[];
    title?: string;
}

interface ProductPageData {
    product?: { name: string };
}

interface SearchPageData {
    title?: string;
}

const useBreadcrumbs = (): BreadcrumbItem[] => {
    const { t } = useTranslation();
    const pageType = useAppSelector(({ page }) => page?.type);
    const pageData = useAppSelector(({ page }) => page?.data, shallowEqual);

    if (pageType === 'contentPage' || pageType === 'categoryPage') {
        const { breadcrumbs, title } = (pageData as ContentPageData) ?? {};
        const ancestorCrumbs = Array.isArray(breadcrumbs)
            ? breadcrumbs.map((breadcrumb) => ({
                  slug: breadcrumb.slug,
                  title: breadcrumb.title,
              }))
            : [];
        if (title) {
            return [...ancestorCrumbs, { slug: null, title }];
        }
        return ancestorCrumbs;
    }

    if (pageType === 'product') {
        const { product } = (pageData as ProductPageData) ?? {};
        return [
            { slug: '/products', title: t('breadcrumbs.all_products') },
            { slug: null, title: product?.name || '' },
        ];
    }

    if (pageType === 'searchPage') {
        const { title } = (pageData as SearchPageData) ?? {};
        return [{ slug: null, title: title ?? '' }];
    }

    return [];
};

const Breadcrumbs = ({ breadcrumbs: breadcrumbsProp, ...rest }: BreadcrumbsProps) => {
    const { t } = useTranslation();
    const { headerNavigationState } = useHeaderNavigationContext();
    const pageType = useAppSelector(({ page }) => page?.type);
    const hookBreadcrumbs = useBreadcrumbs();
    const rawHeaderTheme =
        (useAppSelector(
            (state) => (state.page?.data?.settings as { headerTheme?: string } | undefined)?.headerTheme,
        ) as string | undefined) ?? 'light';
    const cmsHeaderTheme: ThemeName = validThemes.includes(rawHeaderTheme) ? (rawHeaderTheme as ThemeName) : 'light';
    const navMenuOpen = headerNavigationState.mobileMenuActive || headerNavigationState.desktopMenuActive;
    const searchOrStoreOpen = headerNavigationState.searchActive || headerNavigationState.storeFinderActive;
    const isOverlayOpen = navMenuOpen || searchOrStoreOpen;
    const breadcrumbs = breadcrumbsProp ?? hookBreadcrumbs;
    const isAbsoluteLayout = pageType === 'product' || pageType === 'contentPage';

    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        return null;
    }

    const breadCrumbsWithStartPage: BreadcrumbItem[] = [{ slug: '/', title: t('breadcrumbs.home') }, ...breadcrumbs];

    const LayoutWrapper = isAbsoluteLayout ? AbsoluteWrapper : FlowWrapper;

    return (
        <LayoutWrapper
            data-theme={cmsHeaderTheme}
            zIndex={isOverlayOpen ? 'var(--zindex-page-under-header-chrome)' : 'var(--zindex-page-above-header)'}
            {...rest}
        >
            <StyledBreadcrumbs>
                {breadCrumbsWithStartPage.map(({ slug, title }, index) => {
                    const key = `breadcrumb-${slug}-${index}`;
                    const isLast = index + 1 === breadCrumbsWithStartPage.length;
                    return (
                        <Fragment key={key}>
                            {index > 0 && (
                                <Separator>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        /
                                    </Text>
                                </Separator>
                            )}
                            {isLast ? (
                                <CurrentItem>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        {decodeHTMLEntities(title)}
                                    </Text>
                                </CurrentItem>
                            ) : (
                                <LinkItem to={slug}>
                                    <Text as="span" typography="Primary/12_120_510_-1">
                                        {decodeHTMLEntities(title)}
                                    </Text>
                                </LinkItem>
                            )}
                        </Fragment>
                    );
                })}
            </StyledBreadcrumbs>
        </LayoutWrapper>
    );
};

export default Breadcrumbs;
